import * as THREE from 'three'
import { TweenLite, Power2, Power3 } from 'gsap'
import EventEmitter from '../Utils/EventEmitter.js'

export default class GuidedCamera extends EventEmitter
{
    constructor(_options)
    {
        super()

        this.config = _options.config
        this.$eventHub = _options.$eventHub
        this.mobile = _options.mobile
        this.time = _options.time
        this.cursor = _options.cursor
        this.sizes = _options.sizes
        this.ui = _options.ui
        this.route = _options.route

        this.type = 'guided'
        this.scrollFallback = this.config.platform.browser.name === 'ie' && this.config.platform.browser.version === '11.0'

        this.container = new THREE.Object3D()

        this.setOrientation()
        this.setValues()
        this.setStates()
        this.setTransition()
        this.setFocus()
        this.setScroll()
        this.setAngles()
        this.setPan()
        this.setInstance()
    }

    /**
     * Set orientation
     */
    setOrientation()
    {
        this.orientation = this.sizes.viewport.width < this.sizes.viewport.height ? 'portrait' : 'landscape'

        this.sizes.on('resize', () =>
        {
            this.orientation = this.sizes.viewport.width < this.sizes.viewport.height ? 'portrait' : 'landscape'
        })
    }

    /**
     * Set values
     */
    setValues()
    {
        this.position = {}
        this.position.value = {}
        this.position.value.x = 0
        this.position.value.y = 0
        this.position.value.z = 0
        this.position.target = {}
        this.position.target.x = 0
        this.position.target.y = 0
        this.position.target.z = 0
        this.position.easing = 0.008

        this.rotation = {}
        this.rotation.value = {}
        this.rotation.value.x = 0
        this.rotation.value.y = 0
        this.rotation.value.z = 0
        this.rotation.target = {}
        this.rotation.target.x = 0
        this.rotation.target.y = 0
        this.rotation.target.z = 0
        this.rotation.easing = 0.008

        this.time.on('tick', () =>
        {
            const delta = this.time.delta

            // Scroll destination
            const scrollDestination = this.scroll.destination ? this.states.all[this.orientation][this.scroll.destination] : this.states.all[this.orientation][this.states.name]

            // Position (add scroll)
            const positionTarget = {
                x: this.position.target.x,
                y: this.position.target.y,
                z: this.position.target.z
            }

            const interpolatedScrollPosition = {
                x: positionTarget.x + (scrollDestination.position.x - positionTarget.x) * this.scroll.value,
                y: positionTarget.y + (scrollDestination.position.y - positionTarget.y) * this.scroll.value,
                z: positionTarget.z + (scrollDestination.position.z - positionTarget.z) * this.scroll.value
            }

            this.position.value.x += (interpolatedScrollPosition.x - this.position.value.x) * delta * this.position.easing
            this.position.value.y += (interpolatedScrollPosition.y - this.position.value.y) * delta * this.position.easing
            this.position.value.z += (interpolatedScrollPosition.z - this.position.value.z) * delta * this.position.easing

            this.focus.container.position.x = this.position.value.x
            this.focus.container.position.y = this.position.value.y
            this.focus.container.position.z = this.position.value.z

            // Rotation (add scroll)
            const rotationTarget = {
                x: this.rotation.target.x,
                y: this.rotation.target.y,
                z: this.rotation.target.z
            }

            const interpolatedScrollRotation = {
                x: rotationTarget.x + (scrollDestination.rotation.x - rotationTarget.x) * this.scroll.value,
                y: rotationTarget.y + (scrollDestination.rotation.y - rotationTarget.y) * this.scroll.value,
                z: rotationTarget.z + (scrollDestination.rotation.z - rotationTarget.z) * this.scroll.value
            }

            this.rotation.value.x += (interpolatedScrollRotation.x - this.rotation.value.x) * delta * this.rotation.easing
            this.rotation.value.y += (interpolatedScrollRotation.y - this.rotation.value.y) * delta * this.rotation.easing
            this.rotation.value.z += (interpolatedScrollRotation.z - this.rotation.value.z) * delta * this.rotation.easing

            this.focus.container.rotation.x = this.rotation.value.x
            this.focus.container.rotation.y = this.rotation.value.y
            this.focus.container.rotation.z = this.rotation.value.z
        })
    }

    /**
     * Set states
     */
    setStates()
    {
        this.states = {}
        this.states.currentName = null
        this.states.current = null
        this.states.all = {
            landscape:
            {
                protectPreIntro:
                {
                    position: { x: 0.18, y: 0.12, z: - 80.41 },
                    rotation: { x: 0, y: Math.PI, z: 0 },
                    pan: { x: 0.02, y: 0.02 },
                    angles: null
                },
                protectUnfocus:
                {
                    position: { x: 0.24, y: 0.1, z: - 5.8 },
                    rotation: { x: 0, y: 3.24, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles:
                    [
                        { position: { x: - 0.115, y: - 0.035, z: - 0.32 }, rotation: { x: Math.PI * 0.02, y: Math.PI * 0.05, z: 0 } },
                        { position: { x: - 0.5, y: 0, z: - 0.6 }, rotation: { x: - Math.PI * 0.03, y: - Math.PI * 0.55, z: 0 } },
                        { position: { x: - 0.05, y: - 0.06, z: 0.05 }, rotation: { x: 0, y: Math.PI * 0.07, z: 0 } },
                        { position: { x: - 0.17, y: 0.4, z: - 0.5 }, rotation: { x: - Math.PI * 0.5, y: 0, z: 0 } }
                    ]
                },
                protectFocus:
                {
                    position: { x: 0.2360, y: 0.1587, z: -5.7221 },
                    rotation: { x: -0.2369, y: 3.666, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                protectExperience:
                {
                    position: { x: - 0.177, y: - 2.687, z: - 6.450 },
                    rotation: { x: - 0.676, y: 3.665, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                },
                validateUnfocus:
                {
                    position: { x: - 0.32, y: 0.12, z: - 5.8 },
                    rotation: { x: 0, y: 2.14, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles:
                    [
                        { position: { x: 0.04, y: 0, z: - 0.13 }, rotation: { x: 0.07, y: 0.5, z: 0 } },
                        { position: { x: - 0.68, y: 0.17, z: 0.23 }, rotation: { x: - 0.16, y: - 0.49, z: 0 } },
                        // { position: { x: - 0.5, y: 0, z: - 0.6 }, rotation: { x: - Math.PI * 0.05, y: - Math.PI * 0.55, z: 0 } },
                        // { position: { x: - 0.2, y: 0.4, z: - 0.5 }, rotation: { x: - Math.PI * 0.5, y: 0, z: 0 } }
                    ]
                },
                validateFocus:
                {
                    position: { x: - 0.264, y: 0.1487, z: -5.7221 },
                    rotation: { x: 0, y: 2.25, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                validateExperience:
                {
                    position: { x: - 0.177, y: - 2.687, z: - 6.450 },
                    rotation: { x: - 0.676, y: 3.665, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                },
                trainingUnfocus:
                {
                    position: { x: 0.18, y: 0.5, z: - 5.8 },
                    rotation: { x: - 0.25, y: 3.64, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles:
                    [
                        { position: { x: - 0.05, y: - 0.06, z: - 0.9 }, rotation: { x: Math.PI * 0.02, y: Math.PI * 0.15, z: Math.PI * 0.02 } },
                        { position: { x: - 0.8, y: - 0.11, z: - 0.4 }, rotation: { x: Math.PI * 0.04, y: - Math.PI * 0.15, z: - Math.PI * 0.04 } },
                        { position: { x: - 0.2, y: 0.3, z: - 2.0 }, rotation: { x: - Math.PI * 0.1, y: - Math.PI * 1, z: 0 } },
                        { position: { x: - 0.3, y: 1, z: - 0.95 }, rotation: { x: - Math.PI * 0.4, y: 0, z: - Math.PI * 0.16 } }
                    ]
                },
                trainingFocus:
                {
                    position: { x: 0.6, y: 0.35, z: - 5.8 },
                    rotation: { x: - 0.15, y: 3.7, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                trainingExperience:
                {
                    position: { x: - 0.177, y: - 11.687, z: - 6.450 },
                    rotation: { x: - 0.676, y: 3.665, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                },
                investigateUnfocus:
                {
                    position: { x: -0.472, y: 2.713, z: - 6.2 },
                    rotation: { x: - 0.55, y: 3.34, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles:
                    [
                        { position: { x: 0.07, y: - 0.16, z: - 0.88 }, rotation: { x: 0.14, y: 0.63, z: 0.33 } },
                        { position: { x: - 0.62, y: - 0.13, z: - 1.37 }, rotation: { x: 0.4, y: - 1.24, z: - 0.49 } },
                        { position: { x: - 0.32, y: 0.43, z: - 1.86 }, rotation: { x: - 0.78, y: - 2.48, z: - 0.36 } },
                        { position: { x: - 0.22, y: 1.08, z: - 0.39 }, rotation: { x: - 0.81, y: 0, z: 0.69 } }
                    ]
                },
                investigateFocus:
                {
                    position: { x: 0.872, y: 2.513, z: - 5.6 },
                    rotation: { x: - 0.55, y: 2.34, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                investigateExperience:
                {
                    position: { x: - 0.177, y: - 43.687, z: - 6.450 },
                    rotation: { x: - 0.676, y: 3.665, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                }
            },
            portrait:
            {
                protectPreIntro:
                {
                    position: { x: 0.18, y: 0.2, z: - 5.8 },
                    rotation: { x: 0, y: 3.24, z: 0 },
                    pan: { x: 0.02, y: 0.02 },
                    angles: null
                },
                protectUnfocus:
                {
                    position: { x: 0.089, y: 0.151, z: - 5.797 },
                    rotation: { x: - 0.275, y: 3.665, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                protectFocus:
                {
                    position: { x: 0.221, y: 0.184, z: - 5.890 },
                    rotation: { x: - 0.296, y: 3.417, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                protectExperience:
                {
                    position: { x: - 0.177, y: - 2.687, z: - 6.450 },
                    rotation: { x: - 0.676, y: 3.665, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                },
                validateUnfocus:
                {
                    position: { x: - 0.2, y: 0.085, z: - 5.9 },
                    rotation: { x: 0, y: 2.32, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                validateFocus:
                {
                    position: { x: - 0.1, y: 0.07, z: - 5.84 },
                    rotation: { x: 0, y: 2.14, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                validateExperience:
                {
                    position: { x: - 0.279, y: - 2.527, z: - 6.650 },
                    rotation: { x: - 0.676, y: 3.64, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                },
                trainingUnfocus:
                {
                    position: { x: - 0.1, y: 0.5, z: - 7 },
                    rotation: { x: - 0.25, y: 3.64, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                trainingFocus:
                {
                    position: { x: 0.1, y: 0.35, z: - 6.4 },
                    rotation: { x: - 0.15, y: 3.7, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                trainingExperience:
                {
                    position: { x: - 0.777, y: - 11.087, z: - 7.450 },
                    rotation: { x: - 0.676, y: 3.665, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                },
                investigateUnfocus:
                {
                    position: { x: -0.462, y: 2.713, z: - 6.6 },
                    rotation: { x: - 0.55, y: 3.49, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                investigateFocus:
                {
                    position: { x: 1.13, y: 2.713, z: - 6.1 },
                    rotation: { x: - 0.55, y: 2.34, z: 0 },
                    pan: { x: 0.06, y: 0.04 },
                    angles: null
                },
                investigateExperience:
                {
                    position: { x: - 0.177, y: - 43.687, z: - 6.450 },
                    rotation: { x: - 0.676, y: 3.665, z: 0 },
                    pan: { x: 0, y: 0 },
                    angles: null
                }
            }
        }
    }

    /**
     * Set state
     */
    setState(_stateName = '', _duration = 3, _ease = null)
    {
        // Find state
        const state = this.states.all[this.orientation][_stateName]

        // Duration
        let duration = _duration
        if(this.stateName === null)
        {
            duration = 0
        }

        // State not found
        if(typeof state === 'undefined')
        {
            return
        }

        // Save
        this.states.current = state

        // Ease
        const ease = !_ease ? Power2.easeIntOut : _ease

        // Animated
        if(duration > 0)
        {
            // Kill tweens
            TweenLite.killTweensOf(this.position.target)
            TweenLite.killTweensOf(this.rotation.target)
            TweenLite.killTweensOf(this.pan.amplitude)

            // Animate
            TweenLite.to(this.position.target, duration, { x: state.position.x, y: state.position.y, z: state.position.z, ease: ease })
            TweenLite.to(this.rotation.target, duration, { x: state.rotation.x, y: state.rotation.y, z: state.rotation.z, ease: ease })
            TweenLite.to(this.pan.amplitude, 2, { x: state.pan.x, y: state.pan.y })
        }

        // Not animated
        else
        {
            this.position.target.x = state.position.x
            this.position.target.y = state.position.y
            this.position.target.z = state.position.z

            this.rotation.target.x = state.rotation.x
            this.rotation.target.y = state.rotation.y
            this.rotation.target.z = state.rotation.z

            this.position.value.x = state.position.x
            this.position.value.y = state.position.y
            this.position.value.z = state.position.z

            this.rotation.value.x = state.rotation.x
            this.rotation.value.y = state.rotation.y
            this.rotation.value.z = state.rotation.z

            this.pan.amplitude.x = state.pan.x
            this.pan.amplitude.y = state.pan.y
        }

        // Save
        this.states.name = _stateName
    }

    /**
     * Set transition
     */
    setTransition()
    {
        this.transition = {}
        this.transition.distanceBetween = 60
        this.transition.tween = null
        this.transition.container = new THREE.Object3D()
        this.container.add(this.transition.container)

        const index = typeof this.route.current.meta.sliderIndex !== 'undefined' ? this.route.current.meta.sliderIndex : 0
        this.transition.container.position.z = this.transition.distanceBetween * index

        this.$eventHub.$on('experience.changeIndex', (_index) =>
        {
            let easing = Power3.easeInOut
            if(this.transition.tween)
            {
                if(this.transition.tween.ratio < 0.9)
                {
                    this.transition.tween.kill()
                    easing = Power3.easeOut
                }
            }
            this.transition.tween = TweenLite.to(
                this.transition.container.position,
                3,
                {
                    z: this.transition.distanceBetween * _index,
                    ease: easing,
                    onUpdate: () =>
                    {
                        const progressRatio = (this.transition.container.position.z % this.transition.distanceBetween) / this.transition.distanceBetween
                        const sinRatio = Math.sin(progressRatio * Math.PI)
                        const elevation = sinRatio * 0.6
                        const turbulencesY = sinRatio * Math.sin(progressRatio * 50) * 0.02
                        const turbulencesX = sinRatio * Math.sin(progressRatio * 40) * 0.02

                        this.transition.container.position.y = elevation + turbulencesY
                        this.transition.container.position.x = turbulencesX
                    },
                    onComplete: () =>
                    {
                        this.$eventHub.$emit('experience.camera.transitionEnd')
                    }
                }
            )

            this.$eventHub.$emit('experience.camera.transitionStart')
        })
    }

    /**
     * Set focus
     */
    setFocus()
    {
        this.focus = {}
        this.focus.container = new THREE.Object3D()
        this.focus.container.rotation.order = 'YXZ'
        this.transition.container.add(this.focus.container)
    }

    /**
     * Set scroll
     */
    setScroll()
    {
        // Set up
        this.scroll = {}

        this.scroll.$heightElement = document.querySelector('#app')
        this.scroll.destination = 'protectExperience'
        this.scroll.value = 0
        this.scroll.easing = 0.005
        this.scroll.amplitude = {}
        this.scroll.amplitude.positionY = 0
        this.scroll.amplitude.rotationX = 0

        this.scroll.measures = {}
        this.scroll.measures.bodyHeight = 600
        this.scroll.measures.winHeight = 600
        this.scroll.measures.top = 0
        this.scroll.measures.progress = 0

        // Scroll event
        this.scroll.scrollCallback = () =>
        {
            if(this.scrollFallback)
            {
                this.scroll.measures.top = document.body.scrollTop
            }

            else
            {
                this.scroll.measures.top = window.pageYOffset || document.documentElement.scrollTop
            }

            const progress = this.scroll.measures.top / (this.scroll.measures.bodyHeight - this.scroll.measures.winHeight)

            if(!isNaN(progress) && progress !== Infinity)
            {
                this.scroll.measures.progress = progress
            }
        }

        if(this.scrollFallback)
        {
            document.body.addEventListener('scroll', this.scroll.scrollCallback)
        }
        else
        {
            window.addEventListener('scroll', this.scroll.scrollCallback)
        }

        // Resize event
        this.scroll.resizeCallback = () =>
        {
            if(this.scrollFallback)
            {
                this.scroll.measures.bodyHeight = this.scroll.$heightElement.offsetHeight
            }
            else
            {
                this.scroll.measures.bodyHeight = this.config.scrollType === 'body' ? document.querySelector('#app').offsetHeight : document.body.offsetHeight
            }

            this.scroll.measures.winHeight = this.sizes.height

            if(this.scrollFallback)
            {
                this.scroll.measures.top = document.body.scrollTop
            }
            else
            {
                this.scroll.measures.top = window.pageYOffset || document.documentElement.scrollTop
            }

            const progress = this.scroll.measures.top / (this.scroll.measures.bodyHeight - this.scroll.measures.winHeight)

            if(!isNaN(progress) && progress !== Infinity)
            {
                this.scroll.measures.progress = progress
            }
        }
        window.addEventListener('resize', this.scroll.resizeCallback)
        this.scroll.resizeCallback()

        // Time tick
        this.time.on('tick', () =>
        {
            this.scroll.value = this.scroll.measures.progress
        })

        // Update body height event
        this.$eventHub.$on('experience.updateBodyHeight', () =>
        {
            this.scroll.resizeCallback()
        })
    }

    /**
     * Set pan
     */
    setPan()
    {
        // Set up
        this.pan = {}

        this.pan.value = {}
        this.pan.value.x = 0
        this.pan.value.y = 0

        this.pan.target = {}
        this.pan.target.x = 0
        this.pan.target.y = 0

        this.pan.easing = 0.002
        this.pan.amplitude = {}
        this.pan.amplitude.x = 0
        this.pan.amplitude.y = 0

        this.pan.container = new THREE.Object3D()
        this.angles.container.add(this.pan.container)

        if(this.mobile)
        {
            this.pan.deviceOrientationCallback = (event) =>
            {
                let x = 0
                let y = 0

                switch(window.orientation)
                {
                    case 90:
                        x = event.beta * 0.04
                        y = (- event.gamma * 0.02 - Math.PI * 0.32)
                        break

                    case -90:
                        x = - event.beta * 0.04
                        y = (event.gamma * 0.02 - Math.PI * 0.32)
                        break

                    default:
                    case 0:
                        x = event.gamma * 0.03
                        y = (event.beta * 0.02 - Math.PI * 0.32)
                        break
                }

                // Limit X
                if(x > 0.5)
                {
                    x = 0.5
                }
                else if(x < - 0.5)
                {
                    x = - 0.5
                }

                // Limit Y
                if(y > 0.5)
                {
                    y = 0.5
                }
                else if(y < - 0.5)
                {
                    y = - 0.5
                }

                x *= this.pan.amplitude.x
                y *= - this.pan.amplitude.y

                this.pan.target.x = x
                this.pan.target.y = y
            }
            window.addEventListener('deviceorientation', this.pan.deviceOrientationCallback, false)
        }

        // Time tick
        this.time.on('tick', () =>
        {
            const delta = this.time.delta

            if(!this.mobile)
            {
                this.pan.target.x = this.cursor.ratio.fromCenter.x * this.pan.amplitude.x
                this.pan.target.y = - this.cursor.ratio.fromCenter.y * this.pan.amplitude.y
            }

            this.pan.value.x += (this.pan.target.x - this.pan.value.x) * this.pan.easing * delta
            this.pan.value.y += (this.pan.target.y - this.pan.value.y) * this.pan.easing * delta

            this.pan.container.position.x = this.pan.value.x
            this.pan.container.position.y = this.pan.value.y

            this.pan.container.rotation.y = this.pan.value.x
            this.pan.container.rotation.x = this.pan.value.y
        })
    }

    /**
     * Set angles
     */
    setAngles()
    {
        this.angles = {}
        this.angles.active = false
        this.angles.progress = 0
        this.angles.easing = 0.01
        this.angles.ease = Power3.easeInOut
        this.angles.duration = 1
        this.angles.index = - 1
        this.angles.items = null
        this.angles.container = new THREE.Object3D()
        this.angles.container.rotation.order = 'YXZ'
        this.angles.position = { x: 0, y: 0, z: 0 }
        this.angles.rotation = { x: 0, y: 0, z: 0 }
        this.angles.target = {}
        this.angles.target.position = { x: 0, y: 0, z: 0 }
        this.angles.target.rotation = { x: 0, y: 0, z: 0 }
        this.focus.container.add(this.angles.container)

        this.angles.activate = () =>
        {
            this.angles.active = true
        }

        this.angles.deactivate = () =>
        {
            this.angles.active = false

            this.angles.index = - 1

            // Reset
            TweenLite.to(this.angles.target.position, this.angles.duration, { x: 0, y: 0, z: 0, ease: this.angles.ease })
            TweenLite.to(this.angles.target.rotation, this.angles.duration, { x: 0, y: 0, z: 0, ease: this.angles.ease })
        }

        // Time tick
        this.time.on('tick', () =>
        {
            if(this.states.current)
            {
                if(this.states.current.angles !== this.angles.items)
                {
                    this.angles.items = this.states.current.angles

                    this.trigger('anglesChanged', [this.angles.items])
                }

                // Has angles
                if(this.angles.active && this.angles.items)
                {
                    // Get index
                    let index = Math.round(this.angles.progress * this.states.current.angles.length)
                    if(index >= this.states.current.angles.length)
                    {
                        index = 0
                    }

                    // Index changed
                    if(index !== this.angles.index)
                    {
                        this.angles.index = index

                        const coordinates = this.states.current.angles[this.angles.index]

                        TweenLite.to(this.angles.target.position, this.angles.duration, { ...coordinates.position, ease: this.angles.ease })
                        TweenLite.to(this.angles.target.rotation, this.angles.duration, { ...coordinates.rotation, ease: this.angles.ease })

                        this.trigger('anglesIndexChanged', [this.angles.index])
                    }
                }

                // Easing
                this.angles.position.x += (this.angles.target.position.x - this.angles.position.x) * this.time.delta * this.angles.easing
                this.angles.position.y += (this.angles.target.position.y - this.angles.position.y) * this.time.delta * this.angles.easing
                this.angles.position.z += (this.angles.target.position.z - this.angles.position.z) * this.time.delta * this.angles.easing
                this.angles.rotation.x += (this.angles.target.rotation.x - this.angles.rotation.x) * this.time.delta * this.angles.easing
                this.angles.rotation.y += (this.angles.target.rotation.y - this.angles.rotation.y) * this.time.delta * this.angles.easing
                this.angles.rotation.z += (this.angles.target.rotation.z - this.angles.rotation.z) * this.time.delta * this.angles.easing

                // Update values
                this.angles.container.position.x = this.angles.position.x
                this.angles.container.position.y = this.angles.position.y
                this.angles.container.position.z = this.angles.position.z
                this.angles.container.rotation.x = this.angles.rotation.x
                this.angles.container.rotation.y = this.angles.rotation.y
                this.angles.container.rotation.z = this.angles.rotation.z
            }
        })

        // if(this.ui)
        // {
        //     // Folder
        //     const folder = this.ui.addFolder('camera')
        //     folder.open()

        //     // Tweaks
        //     folder
        //         .add(this.angles.container.position, 'x', - 3, 3, 0.01)
        //         .name('position x')

        //     folder
        //         .add(this.angles.container.position, 'y', - 3, 3, 0.01)
        //         .name('position y')

        //     folder
        //         .add(this.angles.container.position, 'z', - 3, 3, 0.01)
        //         .name('position z')

        //     folder
        //         .add(this.angles.container.rotation, 'x', - 3, 3, 0.01)
        //         .name('rotation x')

        //     folder
        //         .add(this.angles.container.rotation, 'y', - 3, 3, 0.01)
        //         .name('rotation y')

        //     folder
        //         .add(this.angles.container.rotation, 'z', - 3, 3, 0.01)
        //         .name('rotation z')
        // }
    }

    /**
     * Set instance
     */
    setInstance()
    {
        this.instance = new THREE.PerspectiveCamera(35, this.sizes.viewport.width / this.sizes.viewport.height, 0.1, 40)

        this.pan.container.add(this.instance)
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Cameras/GuidedCamera.js